import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { Checkbox } from '@shared/components/ui/checkbox';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isMinimumFeeInvoiceable, MinimumFee } from '../../models/MinimumFee';
import { Card } from './Card';

interface CardListProps {
  fees: MinimumFee[];
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  total?: number;
  estimateItemHeight?: number;
  selectedFeeIds?: Set<string>;
  onToggleFeeSelection?: (fee: MinimumFee) => void;
  onSelectAllFees?: () => void;
  selectedColumns?: string[];
  onViewInvoice: (invoiceId: string) => void;
}

export const CardList = ({
  fees,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  total,
  className = '',
  estimateItemHeight = 140,
  selectedFeeIds,
  onToggleFeeSelection,
  onSelectAllFees,
  selectedColumns,
  onViewInvoice,
}: CardListProps) => {
  const { t } = useTranslation('minimumFeesModule');

  const invoiceableFees = useMemo(() => fees.filter(isMinimumFeeInvoiceable), [fees]);

  const areAllInvoiceableSelected =
    invoiceableFees.length > 0 && invoiceableFees.every((fee) => selectedFeeIds?.has(fee.id));

  const renderFeeCard = (props: RenderItemProps<MinimumFee>) => (
    <Card
      {...props}
      isSelected={selectedFeeIds?.has(props.item.id) || false}
      onToggleSelect={() => onToggleFeeSelection?.(props.item)}
      selectedColumns={selectedColumns}
      onViewInvoice={onViewInvoice}
    />
  );

  const getFeeKey = (fee: MinimumFee) => fee.id;

  return (
    <div>
      {invoiceableFees.length > 0 && (
        <div className="flex items-center gap-2 border-b border-card-border pb-4 pt-2">
          <Checkbox
            checked={areAllInvoiceableSelected}
            onCheckedChange={onSelectAllFees}
            className="w-4 h-4"
            data-testid="select-all-minimum-fees-checkbox"
          />
          <span className="text-sm font-medium">{t('selection.selectAll', 'Seleccionar todos')}</span>
        </div>
      )}
      <VirtualList
        data={fees}
        renderItem={renderFeeCard}
        itemKey={getFeeKey}
        estimateItemHeight={estimateItemHeight}
        height="calc(100vh - 150px)"
        gap={0}
        emptyMessage={t('empty.title', 'No existen cuotas mínimas para los filtros seleccionados.')}
        className={`rounded-lg shadow-sm ${className}`}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={300}
        total={total}
        listClassName="touch-pan-y"
        itemClassName="touch-manipulation"
      />
    </div>
  );
};
